import type { FormAnswerValue, GuestVisibleFormRecord } from '@tov-plus/domain';
import { resolveRenderableForm } from '@tov-plus/domain';
import {
  TovButton,
  msg,
  TovCard,
  getTovFormSubmitState,
  useTovAsyncDebouncedCallback,
  useTovForm,
  useTovFormStore,
  useToast,
} from '@tov-plus/ui';
import { useTovQueryClient } from '@tov-plus/frontend-data';
import { invalidateQueryGroups } from '@tov-plus/frontend-data';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from '@tov-plus/frontend-data';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import {
  buildGuestFormSubmissionAnswers,
  clearGuestFormDraft,
  createGuestFormReceipt,
  getDraftValue,
  getGuestFormLocalDraftSummary,
  getGuestFormSubmitFallback,
  getGuestFormsDraftStorageKey,
  normalizeRemoteFormDrafts,
  removeGuestFormDraft,
  removeGuestFormTimestamp,
  renderFieldInput,
  setGuestFormFeedback,
  setGuestFormTimestamp,
  updateGuestFormDraftValue,
} from './formsPageHelpers';

const formSubmitErrorMessage = msg('pages.forms_page.form_submit_error');

export function FormsPage() {
  const access = useGuestAccess();
  const queryClient = useTovQueryClient();
  const visibleForms = access.status === 'ready' ? access.experience.forms : [];
  const [drafts, setDrafts] = useState<Record<string, Record<string, unknown>>>(
    {},
  );
  const [draftSavedAt, setDraftSavedAt] = useState<Record<string, string>>({});
  const [feedbackMessages, setFeedbackMessages] = useState<
    Record<string, string>
  >({});
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [draftSyncState, setDraftSyncState] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [remoteDraftSavedAt, setRemoteDraftSavedAt] = useState<
    Record<string, string>
  >({});
  const syncFormDrafts = useTovAsyncDebouncedCallback(
    async (input: {
      occasionSlug: string;
      guestToken: string;
      persistedDrafts: Record<string, Record<string, unknown>>;
    }) => {
      setDraftSyncState('saving');
      try {
        const response = await guestApi.updateDraftState(
          input.occasionSlug,
          input.guestToken,
          {
            formDrafts:
              Object.keys(input.persistedDrafts).length > 0
                ? input.persistedDrafts
                : null,
          },
        );
        setDraftSyncState('saved');
        setRemoteDraftSavedAt(
          response.formDraftsUpdatedAt
            ? Object.fromEntries(
                Object.keys(input.persistedDrafts).map((formId) => [
                  formId,
                  response.formDraftsUpdatedAt as string,
                ]),
              )
            : {},
        );
        await invalidateQueryGroups(queryClient, [
          guestQueryKeys.drafts(input.occasionSlug, input.guestToken),
          guestQueryKeys.forms(input.occasionSlug, input.guestToken),
        ]);
      } catch {
        setDraftSyncState('error');
      }
    },
    { wait: 700 },
  );
  const submittedFormsCount = visibleForms.filter(
    (form) => form.submission !== null,
  ).length;
  const participantCompletionRows =
    access.status === 'ready'
      ? access.experience.identitySummary.participantDisplayNames.map(
          (name) => ({
            name,
            submittedFormsCount,
            pendingFormsCount: visibleForms.length - submittedFormsCount,
            draftFormsCount: Object.keys(drafts).length,
          }),
        )
      : [];
  useEffect(() => {
    if (access.status !== 'ready') {
      return;
    }
    const { occasionSlug, guestToken } = access;
    if (!guestToken) {
      return;
    }
    let active = true;
    async function hydrateDrafts() {
      const storedDrafts = window.localStorage.getItem(
        getGuestFormsDraftStorageKey(occasionSlug, guestToken),
      );
      const localDrafts = storedDrafts
        ? (() => {
            try {
              return normalizeRemoteFormDrafts(JSON.parse(storedDrafts));
            } catch {
              window.localStorage.removeItem(
                getGuestFormsDraftStorageKey(occasionSlug, guestToken),
              );
              return {};
            }
          })()
        : {};
      try {
        const remoteDraftState = await guestApi.getDraftState(
          occasionSlug,
          guestToken,
        );
        if (!active) {
          return;
        }
        const remoteDrafts = normalizeRemoteFormDrafts(
          remoteDraftState.formDrafts,
        );
        setDrafts({
          ...remoteDrafts,
          ...localDrafts,
        });
        setRemoteDraftSavedAt(
          remoteDraftState.formDraftsUpdatedAt
            ? Object.fromEntries(
                Object.keys(remoteDrafts).map((formId) => [
                  formId,
                  remoteDraftState.formDraftsUpdatedAt as string,
                ]),
              )
            : {},
        );
        setDraftHydrated(true);
        setDraftSyncState('idle');
      } catch {
        if (!active) {
          return;
        }
        setDrafts(localDrafts);
        setDraftHydrated(true);
        setDraftSyncState('error');
      }
    }
    void hydrateDrafts();
    return () => {
      active = false;
    };
  }, [access.status, access.occasionSlug, access.guestToken]);
  useEffect(() => {
    if (access.status !== 'ready') {
      return;
    }
    const storageKey = getGuestFormsDraftStorageKey(
      access.occasionSlug,
      access.guestToken,
    );
    const visibleFormIds = new Set(visibleForms.map((form) => form.form.id));
    const persistedDrafts = Object.fromEntries(
      Object.entries(drafts).filter(([formId]) => visibleFormIds.has(formId)),
    );
    if (Object.keys(persistedDrafts).length === 0) {
      window.localStorage.removeItem(storageKey);
      return;
    }
    window.localStorage.setItem(storageKey, JSON.stringify(persistedDrafts));
  }, [
    access.status,
    access.occasionSlug,
    access.guestToken,
    visibleForms,
    drafts,
  ]);
  useEffect(() => {
    if (access.status !== 'ready' || !draftHydrated) {
      return;
    }
    const visibleFormIds = new Set(visibleForms.map((form) => form.form.id));
    const persistedDrafts = Object.fromEntries(
      Object.entries(drafts).filter(([formId]) => visibleFormIds.has(formId)),
    );
    void syncFormDrafts({
      occasionSlug: access.occasionSlug,
      guestToken: access.guestToken,
      persistedDrafts,
    });
  }, [
    access.status,
    access.occasionSlug,
    access.guestToken,
    draftHydrated,
    drafts,
    syncFormDrafts,
    visibleForms,
  ]);
  useEffect(() => {
    const draftFormIds = Object.entries(drafts)
      .filter(([, value]) => Object.keys(value).length > 0)
      .map(([formId]) => formId);
    if (draftFormIds.length === 0) {
      return;
    }
    const now = new Date().toISOString();
    setDraftSavedAt((current) => {
      const next = { ...current };
      for (const formId of draftFormIds) {
        next[formId] = now;
      }
      return next;
    });
  }, [drafts]);
  if (access.status !== 'ready') {
    return null;
  }
  if (visibleForms.length === 0) {
    return (
      <StatePanel
        preset="empty"
        actions={
          <>
            <NavLink className="primary-button" to="/rsvp">
              {msg('app.guest_workflows.review_rsvp')}{' '}
            </NavLink>
            <NavLink className="secondary-link" to="/">
              {msg('general.navigation.return_to_overview')}{' '}
            </NavLink>
          </>
        }
      />
    );
  }
  return (
    <div className="page-grid">
      <TovCard variant="content" isWide className="hero-card">
        <p className="page-eyebrow">{msg('general.modules.forms.name')}</p>
        <h2>
          {msg(
            'pages.forms_page.pages_forms_page_complete_the_forms_your_hosts_assigned_to_this',
          )}
        </h2>
        <p>
          {msg(
            'pages.forms_page.pages_forms_page_these_forms_are_filtered_by_your_invite_context',
          )}{' '}
        </p>
        <div className="hero-card__stats">
          <article>
            <strong>{visibleForms.length}</strong>
            <span>
              {msg('pages.forms_page.pages_forms_page_visible_forms')}
            </span>
          </article>
          <article>
            <strong>
              {visibleForms.filter((form) => form.submission !== null).length}
            </strong>
            <span>
              {msg('pages.forms_page.pages_forms_page_submitted_forms')}
            </span>
          </article>
          <article>
            <strong>
              {visibleForms.filter((form) => form.submissionLocked).length}
            </strong>
            <span>
              {msg('pages.forms_page.pages_forms_page_locked_after_submit')}
            </span>
          </article>
        </div>
        <div className="action-row">
          <NavLink className="secondary-link" to="/access">
            {msg('pages.forms_page.pages_forms_page_access_continuity')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/rsvp">
            {msg(
              'pages.forms_page.pages_forms_page_rsvp_and_linked_questions',
            )}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/">
            {msg('app.guest_workflows.guest_overview')}{' '}
          </NavLink>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.forms_page.pages_forms_page_submission_receipts')}
        </p>
        <div className="detail-list">
          <article>
            <strong>
              {msg('pages.forms_page.pages_forms_page_submitted_forms')}
            </strong>
            <span>
              {submittedFormsCount}{' '}
              {msg('pages.forms_page.pages_forms_page_of')}{' '}
              {visibleForms.length}{' '}
              {msg('pages.forms_page.pages_forms_page_visible_form')}{' '}
              {visibleForms.length === 1 ? '' : 's'}{' '}
              {msg(
                'pages.forms_page.pages_forms_page_already_have_a_saved_submission_receipt',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.forms_page.pages_forms_page_multi_device_continuity')}
            </strong>
            <span>
              {msg(
                'pages.forms_page.pages_forms_page_draft_answers_sync_back_to_this_invite_so',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.forms_page.pages_forms_page_draft_sync_state')}
            </strong>
            <span>
              {draftSyncState === 'error'
                ? msg(
                    'pages.forms_page.pages_forms_page_remote_draft_sync_needs_attention_local_draft_continuity_is',
                  )
                : draftSyncState === 'saving'
                  ? msg(
                      'pages.forms_page.pages_forms_page_saving_draft_changes_to_your_invite',
                    )
                  : msg(
                      'pages.forms_page.pages_forms_page_draft_sync_is_available_for_visible_forms',
                    )}
            </span>
          </article>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.forms_page.pages_forms_page_household_completion')}
        </p>
        <div className="detail-list detail-list--stacked">
          {participantCompletionRows.map((item) => (
            <article key={`form-completion:${item.name}`}>
              <strong>{item.name}</strong>
              <span>
                {msg('pages.forms_page.pages_forms_page_submitted_forms')}{' '}
                {item.submittedFormsCount}{' '}
                {msg('pages.forms_page.pages_forms_page_pending_forms')}{' '}
                {item.pendingFormsCount}
              </span>
              <span>
                {msg('pages.forms_page.pages_forms_page_active_draft_forms')}{' '}
                {item.draftFormsCount}
                {msg(
                  'pages.forms_page.pages_forms_page_form_submission_still_respects_the_occasion_scoped_invite',
                )}{' '}
              </span>
            </article>
          ))}
        </div>
      </TovCard>

      {visibleForms.map((visibleForm) => (
        <GuestVisibleFormCard
          key={visibleForm.form.id}
          draftSavedAt={draftSavedAt}
          drafts={drafts}
          draftSyncState={draftSyncState}
          feedbackMessages={feedbackMessages}
          guestToken={access.guestToken}
          localeCode={access.experience.localizationContext.resolvedLocaleCode}
          occasionDisplayName={access.experience.occasion.displayName}
          occasionSlug={access.occasionSlug}
          queryClient={queryClient}
          refreshAccess={access.refresh}
          remoteDraftSavedAt={remoteDraftSavedAt}
          setDraftSavedAt={setDraftSavedAt}
          setDrafts={setDrafts}
          setDraftSyncState={setDraftSyncState}
          setFeedbackMessages={setFeedbackMessages}
          setRemoteDraftSavedAt={setRemoteDraftSavedAt}
          timezone={access.experience.occasion.timezone}
          visibleForm={visibleForm}
        />
      ))}
    </div>
  );
}

function GuestVisibleFormCard(props: {
  draftSavedAt: Record<string, string>;
  drafts: Record<string, Record<string, unknown>>;
  draftSyncState: 'idle' | 'saving' | 'saved' | 'error';
  feedbackMessages: Record<string, string>;
  guestToken: string;
  localeCode: string;
  occasionDisplayName: string;
  occasionSlug: string;
  queryClient: ReturnType<typeof useTovQueryClient>;
  refreshAccess(): void;
  remoteDraftSavedAt: Record<string, string>;
  setDraftSavedAt: Dispatch<SetStateAction<Record<string, string>>>;
  setDrafts: Dispatch<SetStateAction<Record<string, Record<string, unknown>>>>;
  setDraftSyncState: Dispatch<
    SetStateAction<'idle' | 'saving' | 'saved' | 'error'>
  >;
  setFeedbackMessages: Dispatch<SetStateAction<Record<string, string>>>;
  setRemoteDraftSavedAt: Dispatch<SetStateAction<Record<string, string>>>;
  timezone: string;
  visibleForm: GuestVisibleFormRecord;
}) {
  const {
    draftSavedAt,
    drafts,
    draftSyncState,
    feedbackMessages,
    guestToken,
    localeCode,
    occasionDisplayName,
    occasionSlug,
    queryClient,
    refreshAccess,
    remoteDraftSavedAt,
    setDraftSavedAt,
    setDrafts,
    setDraftSyncState,
    setFeedbackMessages,
    setRemoteDraftSavedAt,
    timezone,
    visibleForm,
  } = props;
  const notifications = useToast();
  const initialAnswerRecords = useMemo(
    () =>
      visibleForm.form.fields.map((field) => ({
        fieldId: field.id,
        fieldKey: field.fieldKey,
        value: getDraftValue({
          formId: visibleForm.form.id,
          field,
          draft: drafts,
          existingAnswer:
            visibleForm.submission?.answers.find(
              (answer) => answer.fieldId === field.id,
            )?.value ?? null,
        }),
      })),
    [drafts, visibleForm.form, visibleForm.submission],
  );
  const form = useTovForm({
    defaultValues: {
      answers: initialAnswerRecords,
    },
    onSubmit: async ({ value }) => {
      const renderable = resolveRenderableForm(visibleForm.form, value.answers);
      const submissionAnswers = buildGuestFormSubmissionAnswers({
        formId: visibleForm.form.id,
        renderable,
      });

      props.setFeedbackMessages((current) =>
        setGuestFormFeedback({
          current,
          formId: visibleForm.form.id,
          message: '',
        }),
      );

      try {
        const submission = await guestApi.submitForm(
          occasionSlug,
          guestToken,
          visibleForm.form.id,
          {
            answers: submissionAnswers,
          },
        );
        setDrafts((current) =>
          clearGuestFormDraft({
            current,
            formId: visibleForm.form.id,
          }),
        );
        setDraftSavedAt((current) =>
          setGuestFormTimestamp({
            current,
            formId: visibleForm.form.id,
            timestamp: submission.submittedAt,
          }),
        );
        setRemoteDraftSavedAt((current) =>
          removeGuestFormTimestamp({
            current,
            formId: visibleForm.form.id,
          }),
        );
        try {
          await guestApi.updateDraftState(occasionSlug, guestToken, {
            formDrafts: removeGuestFormDraft({
              current: drafts,
              formId: visibleForm.form.id,
            }),
          });
        } catch {
          setDraftSyncState('error');
        }
        await invalidateQueryGroups(queryClient, [
          guestQueryKeys.forms(occasionSlug, guestToken),
          guestQueryKeys.drafts(occasionSlug, guestToken),
          guestQueryKeys.bootstrap(occasionSlug, guestToken),
        ]);
        refreshAccess();
        notifications.success(
          visibleForm.submission
            ? msg('pages.forms_page.correction_submitted')
            : msg('pages.forms_page.form_submitted'),
          msg('pages.forms_page.your_latest_answers_have_been_saved'),
        );
        setFeedbackMessages((current) =>
          setGuestFormFeedback({
            current,
            formId: visibleForm.form.id,
            message: `Submitted at ${formatGuestDateTime(
              submission.submittedAt,
              localeCode,
              timezone,
            )}.`,
          }),
        );
      } catch (error) {
        notifications.error(
          msg('pages.forms_page.form_submission_failed'),
          getGuestErrorMessage(error, formSubmitErrorMessage),
        );
        setFeedbackMessages((current) =>
          setGuestFormFeedback({
            current,
            formId: visibleForm.form.id,
            message: getGuestErrorMessage(error, formSubmitErrorMessage),
          }),
        );
        throw error;
      }
    },
  });
  const answerRecords = useTovFormStore(
    form.store,
    (state) => state.values.answers,
  );
  const submitState = useTovFormStore(form.store, (state) =>
    getTovFormSubmitState({
      isSubmitSuccessful: state.isSubmitSuccessful,
      isSubmitting: state.isSubmitting,
      submissionAttempts: state.submissionAttempts,
    }),
  );
  const renderable = resolveRenderableForm(visibleForm.form, answerRecords);
  const isSubmitting = submitState === 'submitting';
  const disabled =
    visibleForm.submissionLocked && visibleForm.submission !== null;
  const hasLocalDraft =
    Object.keys(drafts[visibleForm.form.id] ?? {}).length > 0;

  function updateAnswer(input: {
    fieldId: string;
    formId: string;
    value: FormAnswerValue;
  }) {
    form.setFieldValue(
      'answers',
      form.store.state.values.answers.map((answer) =>
        answer.fieldId === input.fieldId
          ? {
              ...answer,
              value: input.value,
            }
          : answer,
      ),
    );
    setDrafts((current) =>
      updateGuestFormDraftValue({
        current,
        fieldId: input.fieldId,
        formId: input.formId,
        value: input.value,
      }),
    );
  }

  return (
    <TovCard variant="content" isWide>
      <p className="page-eyebrow">
        {visibleForm.form.scope === 'event'
          ? msg('pages.forms_page.pages_forms_page_event_form')
          : msg('pages.forms_page.pages_forms_page_occasion_form')}
      </p>
      <h2>{visibleForm.form.displayName}</h2>
      <p>
        {visibleForm.form.description ??
          msg('pages.forms_page.pages_forms_page_no_additional_instructions')}
      </p>
      <div className="hero-card__stats">
        <article>
          <strong>{renderable.completion.completionPercent}%</strong>
          <span>{msg('pages.forms_page.pages_forms_page_completion')}</span>
        </article>
        <article>
          <strong>{renderable.completion.visibleFieldCount}</strong>
          <span>
            {msg('pages.forms_page.pages_forms_page_visible_prompts')}
          </span>
        </article>
        <article>
          <strong>{renderable.completion.requiredFieldCount}</strong>
          <span>{msg('pages.forms_page.pages_forms_page_required_now')}</span>
        </article>
        <article>
          <strong>
            {disabled
              ? msg('general.status.locked')
              : hasLocalDraft
                ? msg('pages.forms_page.pages_forms_page_saved_draft')
                : visibleForm.submission
                  ? msg('pages.forms_page.pages_forms_page_submitted')
                  : msg('general.status.in_progress')}
          </strong>
          <span>{msg('pages.forms_page.pages_forms_page_current_state')}</span>
        </article>
      </div>
      <p>
        {disabled
          ? msg(
              'pages.forms_page.pages_forms_page_this_form_is_locked_after_submission_you_can_review',
            )
          : (getGuestFormLocalDraftSummary({
              draftSavedAt: draftSavedAt[visibleForm.form.id],
              hasLocalDraft,
              localeCode,
              timezone,
            }) ??
            (visibleForm.submission
              ? msg(
                  'pages.forms_page.pages_forms_page_you_can_review_the_latest_submission_and_resubmit_while',
                )
              : msg(
                  'pages.forms_page.pages_forms_page_start_now_and_come_back_later_draft_answers_stay',
                )))}
      </p>
      {visibleForm.submission ? (
        <div className="detail-list">
          <article>
            <strong>
              {msg('pages.forms_page.pages_forms_page_submission_receipt')}
            </strong>
            <span>
              {msg('pages.forms_page.pages_forms_page_submitted_at')}{' '}
              {formatGuestDateTime(
                visibleForm.submission.submittedAt,
                localeCode,
                timezone,
              )}
            </span>
          </article>
          {visibleForm.submission.status === 'correction_requested' ? (
            <article>
              <strong>{msg('pages.forms_page.correction_requested')}</strong>
              <span>
                {visibleForm.submission.correctionReason ??
                  msg(
                    'pages.forms_page.your_host_asked_you_to_review_this_response',
                  )}
              </span>
            </article>
          ) : null}
          {(visibleForm.submission.revisionNumber ?? 1) > 1 ? (
            <article>
              <strong>
                {msg('pages.form_details_page.revision')}{' '}
                {visibleForm.submission.revisionNumber}
              </strong>
              <span>
                {msg(
                  'pages.forms_page.previous_responses_remain_in_the_audit_history',
                )}
              </span>
            </article>
          ) : null}
        </div>
      ) : null}
      {visibleForm.events.length > 0 ? (
        <div className="detail-list">
          {visibleForm.events.map((event) => (
            <article key={event.id}>
              <strong>{event.displayName}</strong>
              <span>{event.eventKey}</span>
            </article>
          ))}
        </div>
      ) : null}

      <form
        className="guest-form"
        onSubmit={async (event) => {
          event.preventDefault();
          try {
            await form.handleSubmit();
          } catch {
            // Feedback is written to the form feedback area.
          }
        }}
      >
        {renderable.sections
          .filter((section) => section.visible)
          .map((section) => (
            <section key={section.section.id} className="editor-section">
              <div className="editor-section__header">
                <div>
                  <p className="page-eyebrow">
                    {msg('general.fields.section.label')}
                  </p>
                  <h3>{section.section.title}</h3>
                  {section.section.description ? (
                    <p>{section.section.description}</p>
                  ) : null}
                </div>
              </div>

              {section.fields
                .filter((field) => field.visible)
                .map((field) => (
                  <label key={field.field.id} className="guest-form__field">
                    <span>
                      {field.field.label}
                      {field.required ? ' *' : ''}
                    </span>
                    {field.field.helpText ? (
                      <small className="guest-form__help">
                        {field.field.helpText}
                      </small>
                    ) : null}
                    {renderFieldInput({
                      formId: visibleForm.form.id,
                      field: field.field,
                      value: field.value,
                      disabled,
                      onValueChange: updateAnswer,
                    })}
                  </label>
                ))}
            </section>
          ))}

        <div className="guest-form__actions">
          <TovButton
            data-testid={`guest-form-submit-${visibleForm.form.id}`}
            disabled={disabled || isSubmitting}
            type="submit"
          >
            {disabled
              ? msg('pages.forms_page.pages_forms_page_submission_locked')
              : isSubmitting
                ? msg('general.status.submitting')
                : msg('pages.forms_page.pages_forms_page_submit_form')}
          </TovButton>
          {!disabled && hasLocalDraft ? (
            <TovButton
              type="button"
              variant="secondary"
              onClick={() => {
                setDrafts((current) =>
                  clearGuestFormDraft({
                    current,
                    formId: visibleForm.form.id,
                  }),
                );
                setDraftSavedAt((current) =>
                  removeGuestFormTimestamp({
                    current,
                    formId: visibleForm.form.id,
                  }),
                );
              }}
            >
              {msg('pages.forms_page.pages_forms_page_reset_local_draft')}{' '}
            </TovButton>
          ) : null}
          <TovButton
            type="button"
            variant="secondary"
            onClick={() => {
              const receipt = createGuestFormReceipt({
                draftSavedAt: draftSavedAt[visibleForm.form.id],
                formDisplayName: visibleForm.form.displayName,
                hasLocalDraft,
                isLocked: disabled,
                occasionDisplayName,
                remoteDraftSavedAt: remoteDraftSavedAt[visibleForm.form.id],
                submittedAt: visibleForm.submission?.submittedAt,
              });
              if (typeof navigator !== 'undefined' && navigator.clipboard) {
                void navigator.clipboard.writeText(receipt);
              }
            }}
          >
            {msg('pages.forms_page.pages_forms_page_copy_receipt')}{' '}
          </TovButton>
          <p
            className={`guest-form__feedback${
              submitState === 'error' ? ' guest-form__feedback--error' : ''
            }`}
          >
            {feedbackMessages[visibleForm.form.id] ||
              getGuestFormSubmitFallback({
                completionComplete: renderable.completion.complete,
                draftSavedAt: draftSavedAt[visibleForm.form.id],
                draftSyncState,
                hasLocalDraft,
                localeCode,
                pendingFieldCount: renderable.completion.pendingFieldIds.length,
                remoteDraftSavedAt: remoteDraftSavedAt[visibleForm.form.id],
                submittedAt: visibleForm.submission?.submittedAt,
                submittedUpdatedAt: visibleForm.submission?.updatedAt,
                timezone,
              })}
          </p>
        </div>
      </form>
    </TovCard>
  );
}
